import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList, Line, ComposedChart, Legend } from "recharts";

// Palette used for the "Monthly Orders by Pharmacy" line chart. One line per
// pharmacy — colors are picked to stay distinct on the dark + light theme.
const PHARM_LINE_COLORS = [
  "#1E6FE0", // blue
  "#DC2626", // red
  "#16A34A", // green
  "#EA580C", // orange
  "#7C3AED", // purple
  "#DB2777", // pink
  "#0891B2", // cyan
  "#CA8A04", // amber
  "#4B5563", // slate
  "#059669", // emerald
];

// Full-history monthly orders per pharmacy with a gray system-wide total bar.
export function MonthlyOrdersChart() {
  // Monthly-trend chart data — fetched from a DEDICATED endpoint that
  // deliberately ignores the range pill (This Week / Last Week / …); the
  // trend chart always shows the full monthly history so admins can see
  // seasonality regardless of what the other charts are scoped to. It
  // still honours the pharmacy + driver filters when set.
  const [monthlyChart, setMonthlyChart] = useState({ rows: [], series: [] });
  useEffect(() => {
    api
      .get("/stats/reports/monthly-orders-by-pharmacy")
      .then((r) => setMonthlyChart({ rows: r.data?.rows || [], series: r.data?.series || [] }))
      .catch(() => setMonthlyChart({ rows: [], series: [] }));
  }, []);
  const monthlyByPharmacy = monthlyChart.rows;
  const pharmacySeries = monthlyChart.series;

  // Sum across every pharmacy for each month so we can render a system-wide
  // "Total Orders" bar behind the per-pharmacy lines. Ignores any non-series
  // keys (month label, etc.) by taking only the values recharts also plots.
  const monthlyByPharmacyWithTotal = useMemo(
    () => monthlyByPharmacy.map((row) => {
      const total = pharmacySeries.reduce((sum, name) => sum + (Number(row[name]) || 0), 0);
      return { ...row, __total: total };
    }),
    [monthlyByPharmacy, pharmacySeries],
  );
  // Chart filter: "all" = lines + gray total bar, "__total" = gray bar only, otherwise one pharmacy.
  const [monthlyView, setMonthlyView] = useState("all");
  const monthlyViewSafe = monthlyView === "all" || monthlyView === "__total" || pharmacySeries.includes(monthlyView) ? monthlyView : "all";
  const pillLabel = (props) => {
                      const { x, y, width, value } = props;
                      if (!value || value <= 0) return null;
                      const label = String(value);
                      const w = Math.max(22, label.length * 8 + 8);
                      const h = 16;
                      const cx = x + width / 2;
                      const rx = cx - w / 2;
                      const ry = y - h - 2;
                      return (
                        <g>
                          <rect
                            x={rx}
                            y={ry}
                            width={w}
                            height={h}
                            rx={4}
                            ry={4}
                            fill="#ffffff"
                            stroke="#e5e7eb"
                            strokeWidth={1}
                          />
                          <text
                            x={cx}
                            y={ry + h / 2 + 4}
                            textAnchor="middle"
                            style={{ fill: "#374151", fontSize: 11, fontWeight: 700 }}
                          >
                            {label}
                          </text>
                        </g>
                      );
                    };

  return (
      <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="chart-monthly-orders-by-pharmacy">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h2 className="font-display font-semibold text-base">
            Monthly Orders by Pharmacy · <span className="text-muted-foreground font-normal">full history</span>
          </h2>
          <Select value={monthlyViewSafe} onValueChange={setMonthlyView}>
            <SelectTrigger className="w-[240px] h-9" data-testid="monthly-chart-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="monthly-chart-filter-all">All pharmacies + Total</SelectItem>
              <SelectItem value="__total" data-testid="monthly-chart-filter-total">
                <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm bg-[#6b7280]" /> Total only (gray bar)</span>
              </SelectItem>
              {pharmacySeries.map((name, i) => (
                <SelectItem key={name} value={name} data-testid={`monthly-chart-filter-${i}`}>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: PHARM_LINE_COLORS[i % PHARM_LINE_COLORS.length] }} /> {name} + Total
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Total orders placed each calendar month per pharmacy — full monthly trend. Use the filter to view one pharmacy against the total. The gray bar sums every pharmacy for that month so you can see the system-wide total at a glance.
        </p>
        <div style={{ width: "100%", height: 360 }}>
          {monthlyByPharmacy.length === 0 || pharmacySeries.length === 0 ? (
            <div className="h-full grid place-items-center text-sm text-muted-foreground">
              No orders in this period.
            </div>
          ) : (
            <ResponsiveContainer>
              <ComposedChart data={monthlyByPharmacyWithTotal} margin={{ top: 28, right: 24, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                {/* Add ~15 % headroom above the tallest bar (min +2) so the
                    white-pill "total" label above the highest month never
                    gets clipped by the chart's top edge. Bug fix 2026-09-24
                    — previously e.g. Aug's "71" label was cut off when the
                    axis auto-fit exactly to the max value.  */}
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12 }}
                  domain={[0, (dataMax) => Math.max(4, Math.ceil((dataMax || 0) * 1.15) + 2)]}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }}
                  // Sort tooltip rows largest → smallest so the pharmacy
                  // leaderboard for the hovered month reads at a glance.
                  // "Total (all pharmacies)" is pinned at the bottom.
                  // Bug fix 2026-09-24 (previously alphabetical).
                  itemSorter={(item) => {
                    if (item?.dataKey === "__total") return Infinity;
                    return -(Number(item?.value) || 0);
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {/* System-wide monthly total — renders BEHIND the pharmacy lines so
                    the per-pharmacy trends stay legible against the bar. */}
                {<Bar
                  dataKey="__total"
                  name="Total (all pharmacies)"
                  fill="#6b7280"
                  fillOpacity={0.75}
                  radius={[4, 4, 0, 0]}
                  barSize={22}
                >
                  {/* White-pill label so the total number stays readable
                      against the pharmacy lines that cross the bar top. */}
                  <LabelList
                    dataKey="__total"
                    position="top"
                    content={pillLabel}
                  />
                </Bar>}
                {monthlyViewSafe !== "all" && monthlyViewSafe !== "__total" && (
                  <Bar
                    dataKey={monthlyViewSafe}
                    name={monthlyViewSafe}
                    fill={PHARM_LINE_COLORS[pharmacySeries.indexOf(monthlyViewSafe) % PHARM_LINE_COLORS.length]}
                    radius={[4, 4, 0, 0]}
                    barSize={22}
                  >
                    <LabelList dataKey={monthlyViewSafe} position="top" content={pillLabel} />
                  </Bar>
                )}
                {monthlyViewSafe === "all" && pharmacySeries.map((name, i) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={PHARM_LINE_COLORS[i % PHARM_LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
  );
}
